import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Megaphone, ChevronDown } from "lucide-react";
import { Card, PageHeader, Field, inputCls, Btn, CheckboxGroup, Empty } from "../../components/dash/Kit";
import UsageMeter, { useUsage } from "../../components/dash/UsageMeter";
import VerifyNotice from "../../components/dash/VerifyNotice";
import AiNotice from "../../components/dash/AiNotice";
import CaptionEditor from "../../components/dash/CaptionEditor";
import { api, errMessage } from "../../lib/api";

const PLATFORMS = ["Instagram", "Facebook", "LinkedIn", "TikTok"];
const EMPTY = { name: "", description: "", price: "", start_date: "", end_date: "", url: "" };

export default function Campaigns() {
  const { usage, reloadUsage } = useUsage();
  const [form, setForm] = useState(EMPTY);
  const [platforms, setPlatforms] = useState(["Instagram"]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [openId, setOpenId] = useState(null);

  const load = () => api.get("/engine/campaigns").then((r) => setCampaigns(r.data)).catch(() => {});
  useEffect(() => {
    load();
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const create = async () => {
    if (!form.name.trim()) {
      toast.error("Tell us what you're promoting.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/engine/campaign", { ...form, platforms });
      setResult(data);
      setForm(EMPTY);
      reloadUsage();
      load();
      toast.success("Your campaign is ready to review.");
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const updateCaption = (id, caption) =>
    setResult((r) => ({ ...r, posts: r.posts.map((p) => (p.id === id ? { ...p, caption } : p)) }));

  return (
    <div data-testid="campaigns-page">
      <PageHeader
        testId="campaigns-header"
        title="Promote something"
        body="Describe your offer once. PostMint builds the announcement, the reminders, the story, a Reel concept and a final-availability post."
      />

      <div className="mb-6">
        <UsageMeter usage={usage} />
      </div>

      <AiNotice className="mb-6" testId="campaigns-ai-notice" />

      <Card testId="campaign-form-card">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="What are you promoting?">
            <input value={form.name} onChange={set("name")} placeholder="Valentine's Dinner" className={inputCls} data-testid="campaign-name-input" />
          </Field>
          <Field label="Price" hint="Leave empty if there isn't one — we never invent prices.">
            <input value={form.price} onChange={set("price")} placeholder="€49.50 per person" className={inputCls} data-testid="campaign-price-input" />
          </Field>
          <Field label="Starts">
            <input type="date" value={form.start_date} onChange={set("start_date")} className={inputCls} data-testid="campaign-start-input" />
          </Field>
          <Field label="Ends">
            <input type="date" value={form.end_date} onChange={set("end_date")} className={inputCls} data-testid="campaign-end-input" />
          </Field>
        </div>
        <div className="mt-4">
          <Field label="Describe it">
            <textarea value={form.description} onChange={set("description")} rows={3} className={inputCls} data-testid="campaign-description-input" />
          </Field>
        </div>
        <div className="mt-4">
          <Field label="Booking or purchase link">
            <input value={form.url} onChange={set("url")} placeholder="https://…" className={inputCls} data-testid="campaign-url-input" />
          </Field>
        </div>
        <div className="mt-4">
          <Field label="Platforms">
            <CheckboxGroup name="campaign-platforms" options={PLATFORMS} value={platforms} onChange={setPlatforms} />
          </Field>
        </div>
        <div className="mt-6">
          <Btn onClick={create} disabled={busy} testId="campaign-create-button">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Megaphone className="h-4 w-4" />}
            {busy ? "Building your campaign…" : "Create Campaign"}
          </Btn>
        </div>
      </Card>

      {result && (
        <div className="mt-6 space-y-5" data-testid="campaign-result">
          <Card testId="campaign-strategy-card">
            <p className="font-display text-lg font-semibold text-slate-900">{result.campaign.name}</p>
            {result.campaign.strategy && <p className="mt-2 text-sm text-slate-600">{result.campaign.strategy}</p>}
          </Card>
          <VerifyNotice items={result.posts?.[0]?.verify} testId="campaign-verify-notice" />
          {result.posts.map((p) => (
            <Card key={p.id} testId={`campaign-post-${p.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="font-display text-base font-semibold text-slate-900">
                    {p.role} · {p.platform} · {p.content_type}
                  </p>
                  {p.post_date && <p className="text-sm text-slate-500">Suggested for {p.post_date}</p>}
                </div>
                <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-semibold text-slate-600">
                  AI Draft
                </span>
              </div>
              <div className="mt-4">
                <CaptionEditor
                  value={p.caption}
                  onChange={(v) => updateCaption(p.id, v)}
                  onUsage={reloadUsage}
                  testIdPrefix={`campaign-caption-${p.id}`}
                />
              </div>
              {p.graphic_note && <p className="mt-3 text-sm text-slate-600">Visual: {p.graphic_note}</p>}
            </Card>
          ))}
        </div>
      )}

      <div className="mt-10">
        <p className="mb-3 font-display text-lg font-semibold text-slate-900">Your campaigns</p>
        {campaigns.length === 0 ? (
          <Empty testId="campaigns-empty" title="No campaigns yet" body="Create one above and it will appear here." />
        ) : (
          <div className="space-y-3" data-testid="campaigns-list">
            {campaigns.map((c) => (
              <Card key={c.id} testId={`campaign-row-${c.id}`}>
                <button
                  type="button"
                  onClick={() => setOpenId(openId === c.id ? null : c.id)}
                  className="flex w-full items-center justify-between gap-3 text-left"
                  data-testid={`campaign-toggle-${c.id}`}
                >
                  <span>
                    <span className="block font-display text-base font-semibold text-slate-900">{c.name}</span>
                    <span className="block text-sm text-slate-500">
                      {c.post_count} posts · {c.platforms?.join(", ")} · {c.start_date || "no start date"}
                    </span>
                  </span>
                  <ChevronDown className={`h-4 w-4 shrink-0 transition-transform ${openId === c.id ? "rotate-180" : ""}`} aria-hidden="true" />
                </button>
                {openId === c.id && (
                  <div className="mt-3 border-t border-slate-100 pt-3 text-sm text-slate-600">
                    {c.strategy || "No strategy note saved."}
                    {c.price && <p className="mt-2">Price: {c.price}</p>}
                    {c.url && <p className="mt-1 break-all">Link: {c.url}</p>}
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
